import React from 'react';
import {
  Button, ButtonText, Text, VStack,
} from '@gluestack-ui/themed';
import { useTranslation } from 'react-i18next';

import { useOralExamQuestionTimer } from '@/common/hooks/oralExam/useOralExamQuestionTimer';
import { serverNow } from '@/common/time/serverNow';

import { getOralExamQuestionPhase, type OralExamQuestionPhase } from './oralExamQuestionPhase';

export type OralExamDriver = 'lecturer' | 'narration_agent' | 'examiner_agent';

type DoneAffordanceProps = {
  driver?: OralExamDriver;
  secondsPerQuestion: number;
  questionMarkerStartedAt?: string | null;
  questionDeadline?: string | null;
  studentDoneAt?: string | null;
  isPaused?: boolean;
  isSignalingDone?: boolean;
  onPress?: () => void;
};

function doneLabel(
  driver: OralExamDriver,
  isSignalingDone: boolean,
  t: (key: string, opts?: Record<string, unknown>) => string,
): string {
  if (isSignalingDone) {
    return t('oralExam.doneAcknowledged', { defaultValue: 'Got it — one moment' });
  }
  if (driver === 'lecturer') {
    return t('oralExam.doneAnswering', { defaultValue: 'Done answering' });
  }
  return t('oralExam.doneNextQuestion', { defaultValue: 'Done' });
}

function isDoneDisabled(
  phase: OralExamQuestionPhase,
  _remainingSeconds: number,
  studentDoneAt?: string | null,
  isPaused?: boolean,
  isSignalingDone?: boolean,
): boolean {
  if (phase === 'reading' || phase === 'pre_read') return true;
  if (studentDoneAt || isPaused || isSignalingDone) return true;
  return false;
}

/** Driver-aware primary "done" affordance for the in-progress exam screen. */
export function DoneAffordance({
  driver = 'lecturer',
  secondsPerQuestion,
  questionMarkerStartedAt,
  questionDeadline,
  studentDoneAt,
  isPaused = false,
  isSignalingDone = false,
  onPress,
}: DoneAffordanceProps) {
  const { t } = useTranslation();
  const phase = getOralExamQuestionPhase(
    questionMarkerStartedAt,
    questionDeadline,
    secondsPerQuestion,
    serverNow(),
  );
  const isAnswering = phase === 'answering';

  const { remainingSeconds } = useOralExamQuestionTimer(
    secondsPerQuestion,
    isAnswering,
    questionDeadline ?? null,
    isPaused,
  );

  const disabled = isDoneDisabled(
    phase,
    remainingSeconds,
    studentDoneAt,
    isPaused,
    isSignalingDone,
  );

  if (!onPress) return null;

  return (
    <VStack space="xs" alignItems="flex-end">
      <Button
        size="lg"
        variant="solid"
        action="primary"
        px="$12"
        minHeight={48}
        onPress={onPress}
        isDisabled={disabled}
        testID="oral-exam-done"
        accessibilityState={{ disabled }}
      >
        <ButtonText>{doneLabel(driver, isSignalingDone, t)}</ButtonText>
      </Button>
      {phase === 'reading' && !studentDoneAt ? (
        <Text size="xs" color="$textSubtle" testID="oral-exam-done-reading-hint">
          {t('oralExam.doneWaitingForReading', {
            defaultValue: 'Unlocks after the AI examiner finishes reading the question.',
          })}
        </Text>
      ) : null}
    </VStack>
  );
}
